import { CartService } from './../../../core/services/cart/cart.service';
import { ShopSelectionStore } from '../../../core/state/shop-selection.store';
import { AdBannerTrackingService } from '../../../core/services/ad-banners/ad-banner-tracking.service';
import {
  Component,
  EventEmitter,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { Product } from '../../models/product.model';
import {
  CommonModule,
  CurrencyPipe,
  registerLocaleData,
} from '@angular/common';
import { ProductCounterComponent } from './product-counter/product-counter.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ProductModalComponent } from '../product-modal/product-modal.component';
import { ResponsiveService } from '../../../core/services/utils/responsive.service';
import { Resident } from '../../models/resident.model';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import * as de from '@angular/common/locales/de';
import { Router } from '@angular/router';
import { NavigationService } from '../../../core/services/utils/navigation.service';
import { AvailabilityEnum } from '../../../core/types/enums';
import { ProductsService } from '../../../core/services/products';
import { firstValueFrom } from 'rxjs';
import { ZeroPriceGuardService } from '../../../core/services/zero-price-guard/zero-price-guard.service';
import { DepositType } from '../../../core/types/enums';
import { AnalyticsService } from '../../../core/services/analytics/analytics.service';
import { SHOP_LISTING_QUERY_PARAMS_RESET } from '../../utils/shop-query-params';

@Component({
  selector: 'app-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  imports: [
    MatSnackBarModule,
    CommonModule,
    ProductCounterComponent,
    MatDialogModule,
    MatIconModule,
  ],
  providers: [
    CurrencyPipe,
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
  ],
})
export class ProductCardComponent implements OnInit, OnChanges {
  private readonly MAX_NAME_CHARS_WITH_DEPOSIT = 70;
  private readonly MAX_NAME_CHARS_WITHOUT_DEPOSIT = 78;
  @Input() product: Product;
  @Input() resident: Resident;
  @Input() isFavourite = false;
  @Input() listOfNeedsProduct = false;
  @Input() productCount: number = 0;
  @Input() valid: boolean = true;
  @Input() itemListName?: string;
  @Output() quantityChange = new EventEmitter<{
    product: Product;
    quantity: number;
  }>();
  favouritePump = false;
  /**
   * The per-cart maximum for a limited 0 EUR promotional article, or `null` when the article
   * carries no limit — in which case the tile behaves exactly as it always has.
   */
  maxQuantity: number | null = null;

  ngOnInit() {
    if (!this.listOfNeedsProduct) {
      this.cartService.getAllCarts().subscribe(() => {
        this.updateProductCount();
      });
    }
    this.refreshMaxQuantity();
  }

  ngOnChanges() {
    if (!this.listOfNeedsProduct) this.updateProductCount();
    this.refreshMaxQuantity();
  }

  constructor(
    private dialog: MatDialog,
    private responsiveService: ResponsiveService,
    private cartService: CartService,
    private router: Router,
    private navigationService: NavigationService,
    private productsService: ProductsService,
    private shopSelectionStore: ShopSelectionStore,
    private zeroPriceGuardService: ZeroPriceGuardService,
    private adBannerTrackingService: AdBannerTrackingService,
    private analyticsService: AnalyticsService
  ) {
    registerLocaleData(de.default);
  }

  get showAlternativeButton(): boolean {
    return (
      this.product.alternativeProductId !== null &&
      this.product.alternativeProductId !== 0
    );
  }

  get unavailabilityText(): string {
    switch (this.product.availability?.status) {
      case AvailabilityEnum.No_permanently:
        return 'AUSGELISTET';
      case AvailabilityEnum.No_temporarily:
        return 'BALD WIEDER DA';
      default:
        return 'NICHT VERFÜGBAR';
    }
  }

  get hasDeposit(): boolean {
    return (
      this.product?.depositType &&
      this.product?.depositType !== DepositType.NONE
    );
  }

  get depositLabel(): string {
    switch (this.product?.depositType) {
      case DepositType.EINWEG_0_25:
        return 'EINWEG';
      case DepositType.MEHRWEG_0_15:
        return 'MEHRWEG';
      case DepositType.MEHRWEG_0_08:
        return 'MEHRWEG';
      default:
        return '';
    }
  }

  get depositAmountText(): string {
    switch (this.product?.depositType) {
      case DepositType.MEHRWEG_0_15:
        return '0,15';
      case DepositType.EINWEG_0_25:
        return '0,25';
      case DepositType.MEHRWEG_0_08:
        return '0,08';
      default:
        return '';
    }
  }

  get isProductNameTruncated(): boolean {
    const name = this.product?.name ?? '';
    return name.length > this.maxNameChars;
  }

  get displayProductName(): string {
    const name = this.product?.name ?? '';
    const maxChars = this.maxNameChars;

    if (name.length <= maxChars) {
      return name;
    }

    const shortened = name.slice(0, maxChars).trimEnd();
    const safeCutIndex = shortened.lastIndexOf(' ');
    const cutAt =
      safeCutIndex > Math.floor(maxChars * 0.6)
        ? safeCutIndex
        : shortened.length;

    return shortened.slice(0, cutAt);
  }

  private get maxNameChars(): number {
    return this.hasDeposit
      ? this.MAX_NAME_CHARS_WITH_DEPOSIT
      : this.MAX_NAME_CHARS_WITHOUT_DEPOSIT;
  }

  updateProductCount() {
    const cart = this.cartService.cart.getResidentCart(this.resident?.id);
    const product = cart?.getProduct(this.product?.id);
    this.productCount = product ? product.quantity : 0;
    this.refreshMaxQuantity();
  }

  openDialog(product: Product): void {
    if (product.isUnavailable() || this.listOfNeedsProduct) return;
    if (this.itemListName) {
      this.analyticsService.trackSelectItem(this.itemListName, product);
    }
    this.dialog.open(ProductModalComponent, {
      data: { productId: product.id },
      width: '900px',
      autoFocus: false,
      height: 'auto',
      maxHeight: '100vh',
      panelClass: 'product-modal',
    });
  }

  counterClick(event: Event): void {
    event.stopPropagation();
  }

  get quantityLimitText(): string {
    return this.maxQuantity === null ? '' : `Max. ${this.maxQuantity} Stück`;
  }

  /**
   * Resolved on the events that can change it rather than read through a getter, so the
   * counter's input is not recomputed on every change detection pass.
   */
  private refreshMaxQuantity(): void {
    this.maxQuantity = this.zeroPriceGuardService.maxQuantityFor({
      product: this.product,
      resident: this.resident,
    });
  }

  canIncrease = (currentValue: number, nextValue: number): boolean => {
    return this.zeroPriceGuardService.canIncrease({
      product: this.product,
      resident: this.resident,
      currentValue,
      nextValue,
    });
  };

  onCounterChange(event): void {
    if (this.listOfNeedsProduct) {
      this.productCount = event.value;

      this.quantityChange.emit({
        product: this.product,
        quantity: event.value,
      });

      return;
    }
    let residentCart = this.cartService.cart.getResidentCart(this.resident.id);
    this.cartService.setUpdate();
    if (!residentCart) {
      residentCart = this.cartService.cart.createNewResidentCart(
        this.resident.id
      );
    }
    if (event.value === 0) {
      this.cartService.cart.removeProductInResidentCart(
        this.resident.id,
        this.product.id
      );
      return;
    }
    if (!residentCart.getProduct(this.product.id)) {
      this.cartService.cart.addProductToResidentCart(
        this.resident.id,
        this.product.convertToCartProduct()
      );
    }

    this.cartService.cart.changeResidentCartProductQuantity(
      this.resident.id,
      this.product.id,
      event.value
    );
    this.adBannerTrackingService.productWasAdded(
      this.cartService.cart.cartId,
      this.product.id
    );
    if (this.itemListName) {
      this.analyticsService.trackSelectItem(this.itemListName, this.product);
    }
  }

  showAlternative(): void {
    if (this.listOfNeedsProduct) {
      this.productsService
        .getProductsByIds([this.product.alternativeProductId])
        .subscribe(alternativeProducts => {
          this.quantityChange.emit({
            product: alternativeProducts.data[0],
            quantity: 1,
          });
          this.quantityChange.emit({
            product: this.product,
            quantity: 0,
          });
        });
    } else {
      this.navigationService.setPreviousUrl(this.router.url);
      this.router.navigate(['/shop/search'], {
        queryParams: {
          ...SHOP_LISTING_QUERY_PARAMS_RESET,
          alternativeFor: this.product.id,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  removeInvalidProduct(event: Event): void {
    event.stopPropagation();
    this.quantityChange.emit({
      product: this.product,
      quantity: 0,
    });
  }

  addProductToFavourites(event: Event, product: Product): void {
    event.stopPropagation();
    this.favouritePump = true;
    const favouriteProductsIds = [
      ...(this.resident.favouriteProducts ?? []),
      product.id,
    ];
    this.updateFavourites(favouriteProductsIds, product);
  }

  removeProductFromFavourites(event: Event, product: Product): void {
    event.stopPropagation();
    const favouriteProductsIds = (this.resident.favouriteProducts ?? []).filter(
      id => id !== product.id
    );
    this.updateFavourites(favouriteProductsIds, null);
  }

  onFavouritePumpEnd(): void {
    this.favouritePump = false;
  }

  async updateFavourites(
    favouriteProductsIds: number[],
    product?: Product
  ): Promise<void> {
    const previousFavouriteProductsIds = this.resident.favouriteProducts;
    this.resident.favouriteProducts = favouriteProductsIds;
    try {
      await firstValueFrom(
        this.productsService.updateResidentFavourites(
          this.resident.customerId,
          this.resident.id,
          favouriteProductsIds,
          product
        )
      );
      this.shopSelectionStore.patchResident(this.resident.id, {
        favouriteProducts: favouriteProductsIds,
      });
    } catch {
      this.resident.favouriteProducts = previousFavouriteProductsIds;
    }
  }
}
